import React from 'react';
import PropTypes from 'prop-types';

const shouldShowApprovalLink = (dateType) =>
  dateType === 'Baseline Start Date' || dateType === 'Baseline End Date';

const TimelineDialogShell = ({ title, children, onClose }) => (
  <div
    className="timeline-dialog__backdrop"
    role="presentation"
    onClick={onClose}
  >
    <div
      className="timeline-dialog"
      role="dialog"
      aria-modal="true"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="timeline-dialog__header">
        <h3>{title}</h3>
      </div>
      <div className="timeline-dialog__content">{children}</div>
    </div>
  </div>
);

TimelineDialogShell.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const TimelineRequestDialog = ({
  isOpen,
  mode,
  request,
  dateTypeOptions,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  if (!isOpen) return null;

  const title
    = mode === 'edit'
      ? `Edit Request - ${request.srnNo || ''}`.trim()
      : 'New Request';

  return (
    <TimelineDialogShell title={title} onClose={onClose}>
      <div className="timeline-dialog__grid timeline-dialog__grid--stacked">
        <label className="timeline-dialog__field timeline-dialog__field--full">
          <span>Project Name*</span>
          <input
            type="text"
            value={request.projectName || ''}
            onChange={(event) => onChange('projectName', event.target.value)}
            readOnly={mode === 'edit' || Boolean(request.projectName)}
            disabled={isSubmitting}
            placeholder="Enter project name"
          />
        </label>

        <div className="timeline-dialog__split">
          <label className="timeline-dialog__field">
            <span>Date Type*</span>
            <select
              value={request.requestedFieldValue || ''}
              onChange={(event) =>
                onChange('requestedFieldValue', event.target.value)
              }
              disabled={isSubmitting}
            >
              {dateTypeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="timeline-dialog__field">
            <span>New Date*</span>
            <input
              className="timeline-dialog__date-input"
              type="date"
              value={request.newDateValue || ''}
              placeholder="dd-mm-yyyy"
              onChange={(event) => onChange('newDateValue', event.target.value)}
              disabled={isSubmitting}
            />
          </label>
        </div>

        {shouldShowApprovalLink(request.requestedFieldValue) ? (
          <label className="timeline-dialog__field timeline-dialog__field--full">
            <span>Approval Link*</span>
            <input
              type="text"
              value={request.approvalLink || ''}
              onChange={(event) => onChange('approvalLink', event.target.value)}
              disabled={isSubmitting}
              placeholder="https://drive.google.com"
            />
          </label>
        ) : null}

        <label className="timeline-dialog__field timeline-dialog__field--full">
          <span>Reason for Change</span>
          <input
            type="text"
            value={request.reasonForChange || ''}
            onChange={(event) =>
              onChange('reasonForChange', event.target.value)
            }
            disabled={isSubmitting}
            placeholder="Enter..."
          />
        </label>
      </div>
      <div className="timeline-dialog__footer">
        <button
          type="button"
          className="btn-cancel"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-submit"
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
        </button>
      </div>
    </TimelineDialogShell>
  );
};

TimelineRequestDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['new', 'edit']).isRequired,
  request: PropTypes.shape({
    srnNo: PropTypes.string,
    projectName: PropTypes.string,
    requestedFieldValue: PropTypes.string,
    approvalLink: PropTypes.string,
    newDateValue: PropTypes.string,
    reasonForChange: PropTypes.string,
  }).isRequired,
  dateTypeOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

TimelineRequestDialog.defaultProps = {
  isSubmitting: false,
};

export const TimelineStatusDialog = ({
  isOpen,
  isBulk,
  request,
  selectedCount,
  projectName,
  statusOptions,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  if (!isOpen) return null;

  const title = isBulk
    ? 'Update status'
    : `Update status - ${request?.srnNo || ''}`.trim();

  return (
    <TimelineDialogShell title={title} onClose={onClose}>
      <div className="timeline-dialog__grid">
        <label className="timeline-dialog__field">
          <span>Project Name</span>
          <input
            type="text"
            value={projectName || request?.projectName || ''}
            readOnly
          />
        </label>
        {isBulk ? (
          <label className="timeline-dialog__field">
            <span>No. of Requests Selected</span>
            <input type="text" value={selectedCount} readOnly />
          </label>
        ) : null}
        <label className="timeline-dialog__field timeline-dialog__field--full">
          <span>Request Status</span>
          <select
            value={request?.requestStatus || ''}
            onChange={(event) => onChange('requestStatus', event.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select</option>
            {statusOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label className="timeline-dialog__field timeline-dialog__field--full">
          <span>Comment*</span>
          <textarea
            rows="3"
            value={request?.approverComment || ''}
            onChange={(event) =>
              onChange('approverComment', event.target.value)
            }
            disabled={isSubmitting}
            placeholder="Enter..."
          />
        </label>
      </div>
      <div className="timeline-dialog__footer">
        <button
          type="button"
          className="btn-cancel"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-submit"
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </TimelineDialogShell>
  );
};

TimelineStatusDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isBulk: PropTypes.bool,
  request: PropTypes.shape({
    srnNo: PropTypes.string,
    projectName: PropTypes.string,
    requestStatus: PropTypes.string,
    approverComment: PropTypes.string,
  }),
  selectedCount: PropTypes.number,
  projectName: PropTypes.string,
  statusOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

TimelineStatusDialog.defaultProps = {
  isBulk: false,
  request: null,
  selectedCount: 0,
  projectName: '',
  isSubmitting: false,
};
